import { Package, Camera, MessageSquare } from 'lucide-react';
import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useToast } from "../hooks/use-toast";

const API = process.env.REACT_APP_BACKEND_URL || "";

const STATUS_LABELS = {
  pending:  { label: "En attente de modération", color: "text-amber-700 bg-amber-100" },
  approved: { label: "Publiée", color: "text-green-700 bg-green-100" },
  rejected: { label: "Rejetée", color: "text-red-700 bg-red-100" },
};

// Messagerie d'une annonce
const ChatBox = ({ listingId, listingTitle }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [image, setImage] = useState("");
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem("dz_token");
      const r = await fetch(API + "/api/occasion/messages/" + listingId, {
        headers: { "Authorization": "Bearer " + token },
      });
      if (r.ok) setMessages(await r.json());
    } catch {}
  };

  useEffect(() => { fetchMessages(); const i = setInterval(fetchMessages, 5000); return () => clearInterval(i); }, [listingId]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const uploadImage = async (file) => {
    setUploading(true);
    try {
      const token = localStorage.getItem("dz_token");
      const data = new FormData();
      data.append("file", file);
      const r = await fetch(API + "/api/upload", {
        method: "POST", headers: { "Authorization": "Bearer " + token }, body: data,
      });
      if (r.ok) { const d = await r.json(); setImage(d.url); }
    } catch {}
    setUploading(false);
  };

  const send = async () => {
    if ((!text.trim() && !image) || sending) return;
    setSending(true);
    try {
      const token = localStorage.getItem("dz_token");
      const r = await fetch(API + "/api/occasion/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": "Bearer " + token },
        body: JSON.stringify({ listing_id: listingId, text: text.trim(), image }),
      });
      if (r.ok) { setText(""); setImage(""); fetchMessages(); }
    } catch {}
    setSending(false);
  };

  return (
    <div className="border border-[#E8E0D0] rounded-xl overflow-hidden">
      <div className="bg-[#F5F1EA] px-4 py-2 text-sm font-medium text-[#1F4D3F]">{listingTitle}</div>
      <div className="h-64 overflow-y-auto p-3 space-y-2 bg-[#FAFAF8]">
        {messages.length === 0 && <div className="text-center text-sm text-[#9A8F82] py-8">Aucun message</div>}
        {messages.map(m => (
          <div key={m.id} className={"flex " + (m.from_id === user?.user_id ? "justify-end" : "justify-start")}>
            <div className={"max-w-[80%] px-3 py-2 rounded-xl text-sm " + (m.from_id === user?.user_id ? "bg-[#1F4D3F] text-white" : "bg-white border border-[#E8E0D0] text-[#2D2A26]")}>
              {m.from_id !== user?.user_id && <div className="text-xs font-medium text-[#C75B27] mb-1">{m.from_name}</div>}
              {m.image && <img src={m.image.startsWith("/api") ? API + m.image : m.image} alt="" className="rounded-lg mb-1 max-w-full" style={{maxHeight:"150px"}} />}
              {m.text}
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
      {image && (
        <div className="px-3 py-2 bg-[#F5F1EA] flex items-center gap-2">
          <img src={image.startsWith("/api") ? API + image : image} alt="" className="w-12 h-12 rounded object-cover" />
          <span className="text-xs text-[#7A6F5C] flex-1">Photo prête à envoyer</span>
          <button onClick={() => setImage("")} className="text-red-500 text-sm">Retirer</button>
        </div>
      )}
      <div className="p-3 border-t border-[#E8E0D0] flex gap-2 bg-white">
        <label className="cursor-pointer flex items-center px-2 text-xl" title="Envoyer une photo">
          <Camera className="w-5 h-5 text-[#7A6F5C]" />
          <input type="file" accept="image/*" onChange={e => e.target.files[0] && uploadImage(e.target.files[0])} className="hidden" disabled={uploading} />
        </label>
        <input value={text} onChange={e => setText(e.target.value)} onKeyDown={e => e.key === "Enter" && send()}
          placeholder={uploading ? "Upload photo..." : "Votre message..."}
          className="flex-1 px-3 py-2 border border-[#E8E0D0] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1F4D3F]" />
        <button onClick={send} disabled={(!text.trim() && !image) || sending || uploading}
          className="bg-[#1F4D3F] text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
          {sending ? "..." : "Envoyer"}
        </button>
      </div>
    </div>
  );
};

const MesAnnonces = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [tab, setTab] = useState("annonces");
  const [listings, setListings] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openChat, setOpenChat] = useState(null);

  const loadListings = async () => {
    try {
      const token = localStorage.getItem("dz_token");
      const r = await fetch(API + "/api/occasion/my-listings", { headers: { "Authorization": "Bearer " + token } });
      if (r.ok) setListings(await r.json());
    } catch {}
  };

  const loadConversations = async () => {
    try {
      const token = localStorage.getItem("dz_token");
      const r = await fetch(API + "/api/occasion/conversations", { headers: { "Authorization": "Bearer " + token } });
      if (r.ok) setConversations(await r.json());
    } catch {}
  };

  useEffect(() => {
    if (!user) { navigate("/connexion"); return; }
    Promise.all([loadListings(), loadConversations()]).then(() => setLoading(false));
  }, [user]);

  const deleteListing = async (id) => {
    if (!window.confirm("Supprimer cette annonce ?")) return;
    try {
      const token = localStorage.getItem("dz_token");
      const r = await fetch(API + "/api/occasion/listings/" + id, {
        method: "DELETE", headers: { "Authorization": "Bearer " + token },
      });
      if (r.ok) { toast({ title: "Annonce supprimée" }); loadListings(); }
    } catch {}
  };

  if (!user) return null;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="font-serif text-3xl font-semibold text-[#1F4D3F] mb-6">Mes annonces & messages</h1>

      <div className="flex gap-2 mb-6 border-b border-[#E8E0D0]">
        <button onClick={() => setTab("annonces")}
          className={"px-4 py-2 text-sm font-medium border-b-2 " + (tab === "annonces" ? "border-[#1F4D3F] text-[#1F4D3F]" : "border-transparent text-[#7A6F5C]")}>
          Mes annonces ({listings.length})
        </button>
        <button onClick={() => setTab("messages")}
          className={"px-4 py-2 text-sm font-medium border-b-2 " + (tab === "messages" ? "border-[#1F4D3F] text-[#1F4D3F]" : "border-transparent text-[#7A6F5C]")}>
          Messages ({conversations.length})
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#9A8F82]">Chargement...</div>
      ) : tab === "annonces" ? (
        listings.length === 0 ? (
          <div className="text-center py-12">
            <div className="flex justify-center mb-3"><Package className="w-14 h-14 text-[#C9BFAE]" /></div>
            <p className="text-[#7A6F5C] mb-4">Vous n'avez pas encore d'annonce</p>
            <button onClick={() => navigate("/occasion")} className="bg-[#C75B27] text-white px-6 py-3 rounded-xl font-semibold text-sm">Déposer une annonce</button>
          </div>
        ) : (
          <div className="space-y-3">
            {listings.map(l => {
              const st = STATUS_LABELS[l.status] || STATUS_LABELS.pending;
              return (
                <div key={l.id} className="bg-white border border-[#E8E0D0] rounded-xl p-4 flex items-center gap-4">
                  <div className="w-16 h-16 rounded-lg bg-[#F5F1EA] flex items-center justify-center shrink-0"><Package className="w-10 h-10 text-[#C9BFAE]" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[#2D2A26] truncate">{l.title}</div>
                    <div className="text-sm text-[#7A6F5C]">{l.price ? Math.round(l.price).toLocaleString("fr") + " DA" : "Prix sur demande"}</div>
                    <span className={"text-xs px-2 py-0.5 rounded-full mt-1 inline-block " + st.color}>{st.label}</span>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => navigate("/occasion/" + l.id)} className="text-[#1F4D3F] hover:bg-[#E4EDE9] px-3 py-2 rounded-lg text-sm border border-[#1F4D3F]">Modifier</button>
                    <button onClick={() => deleteListing(l.id)} className="text-red-500 hover:bg-red-50 px-3 py-2 rounded-lg text-sm">Supprimer</button>
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : (
        conversations.length === 0 ? (
          <div className="text-center py-12">
            <div className="flex justify-center mb-3"><MessageSquare className="w-14 h-14 text-[#C9BFAE]" /></div>
            <p className="text-[#7A6F5C]">Aucune conversation</p>
          </div>
        ) : (
          <div className="space-y-3">
            {conversations.map(c => (
              <div key={c.listing_id}>
                <button onClick={() => setOpenChat(openChat === c.listing_id ? null : c.listing_id)}
                  className="w-full bg-white border border-[#E8E0D0] rounded-xl p-4 flex items-center gap-4 hover:border-[#1F4D3F] text-left">
                  <div className="w-12 h-12 rounded-full bg-[#1F4D3F] flex items-center justify-center text-white font-bold shrink-0">
                    {(c.other_name || "?")[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[#2D2A26] truncate">{c.listing_title}</div>
                    <div className="text-sm text-[#7A6F5C] truncate">{c.other_name} : {c.last_message}</div>
                  </div>
                  {c.unread > 0 && <span className="bg-[#C75B27] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">{c.unread}</span>}
                </button>
                {openChat === c.listing_id && (
                  <div className="mt-2">
                    <ChatBox listingId={c.listing_id} listingTitle={c.listing_title} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
};

export default MesAnnonces;
